/**
 * Studios API - Mudo Network
 *
 * Functions for querying and managing studio data from Supabase.
 * Studios are ceramic studios/brands that appear on the Mudo Map.
 */

import { supabase } from "../../supabaseClient";
import type { Database } from "../database.types";

type Studio = Database["public"]["Tables"]["studios"]["Row"];

/**
 * List public studios near a location
 *
 * Uses basic distance calculation to find studios within a radius.
 * For production, consider using PostGIS or Supabase's geometry functions.
 */
export async function listPublicStudiosNearby({
  lat,
  lng,
  radiusKm = 50,
}: {
  lat: number;
  lng: number;
  radiusKm?: number;
}) {
  try {
    // Fetch all public studios with valid coordinates
    const { data, error } = await supabase!
      .from("studios")
      .select("*")
      .eq("is_public", true)
      .not("location_lat", "is", null)
      .not("location_lng", "is", null);

    if (error) throw error;

    // Filter by distance (simple haversine approximation)
    const studiosWithDistance = (data || [])
      .map((studio) => {
        const distance = calculateDistance(
          lat,
          lng,
          studio.location_lat!,
          studio.location_lng!,
        );
        return { ...studio, distance };
      })
      .filter((studio) => studio.distance <= radiusKm)
      .sort((a, b) => a.distance - b.distance);

    return { data: studiosWithDistance, error: null };
  } catch (error) {
    console.error("Error fetching nearby studios:", error);
    return { data: null, error };
  }
}

/**
 * Get a studio by its unique slug
 */
export async function getStudioBySlug(slug: string) {
  try {
    const { data, error } = await supabase!
      .from("studios")
      .select("*")
      .eq("slug", slug)
      .eq("is_public", true)
      .single();

    if (error) {
      // If no studio found, return null instead of throwing
      if ((error as any).code === "PGRST116") {
        return { data: null, error: null };
      }
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error(`Error fetching studio by slug "${slug}":`, error);
    return { data: null, error };
  }
}

/**
 * Get a studio by ID
 */
export async function getStudioById(id: string) {
  try {
    const { data, error } = await supabase!
      .from("studios")
      .select("*")
      .eq("id", id)
      .eq("is_public", true)
      .single();

    if (error) {
      if ((error as any).code === "PGRST116") {
        return { data: null, error: null };
      }
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error(`Error fetching studio by id "${id}":`, error);
    return { data: null, error };
  }
}

/**
 * List all public studios
 */
export async function listPublicStudios({
  city,
  country,
  limit = 100,
}: {
  city?: string;
  country?: string;
  limit?: number;
} = {}) {
  try {
    // ✅ OPTIMIZATION: Thin list query - exclude heavy fields like hero_image_url, description
    // Detail pages (getStudioBySlug, getStudioById) will fetch full records
    let query = supabase!
      .from("studios")
      .select(
        [
          'id',
          'owner_user_id',
          'name',
          'slug',
          'city',
          'country',
          'is_public',
          'created_at',
        ].join(',')
      )
      .eq("is_public", true)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (city) {
      query = query.eq("city", city);
    }

    if (country) {
      query = query.eq("country", country);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error("Error fetching public studios:", error);
    return { data: null, error };
  }
}

/**
 * Create or update a studio (for authenticated studio owners)
 *
 * Important: DB has a unique constraint "studios_owner_unique" on owner_user_id,
 * so we use onConflict: 'owner_user_id' to update the existing row instead of
 * inserting duplicates.
 */
export async function upsertStudio(
  studio: Database["public"]["Tables"]["studios"]["Insert"],
) {
  try {
    // Guard: require auth; RLS / constraints expect a real owner_user_id
    const { data: sessionData } = await supabase!.auth.getSession();
    const uid = sessionData?.session?.user?.id;
    if (!uid) {
      return {
        data: null,
        error: new Error(
          "Not signed in. Please sign in to create or update a studio.",
        ),
      };
    }

    // Ensure owner_user_id is set to the current user
    const payload: Database["public"]["Tables"]["studios"]["Insert"] = {
      ...studio,
      owner_user_id: studio.owner_user_id ?? uid,
    };

    const { data, error } = await supabase!
      .from("studios")
      // IMPORTANT: use owner_user_id as conflict target to respect
      // the unique constraint "studios_owner_unique"
      .upsert(payload, { onConflict: "owner_user_id" })
      .select()
      .single();

    if (error) throw error as any;
    // Ensure owner membership via server API (bypass RLS reliably)
    try {
      const { data: sessionData } = await supabase!.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (token && data?.id && uid) {
        await fetch('/api/memberships/upsert', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ action: 'ensureOwner', studioId: data.id, userId: uid })
        });
      }
    } catch (e) {
      console.warn('[studiosApi] ensureOwner membership failed (non-fatal):', (e as any)?.message || e);
    }

    return { data, error: null };
  } catch (error: any) {
    const code = error?.code || "";
    const message = error?.message || String(error);
    // Map common permission errors to clearer message
    if (code === "42501" || /permission denied/i.test(message)) {
      console.error("Error upserting studio:", error);
      return {
        data: null,
        error: new Error(
          "Permission denied for studios. Ensure you are signed in and that RLS policies exist. Run migrations in Supabase (001_mudo_network_schema.sql).",
        ),
      };
    }
    console.error("Error upserting studio:", error);
    return { data: null, error };
  }
}

/**
 * Create a studio for a given owner with a friendly, unique slug.
 * Minimal required fields per schema: owner_user_id, name, slug.
 */
export async function createStudioForUser({
  name,
  isPublic = false,
}: {
  name: string;
  isPublic?: boolean;
}) {
  try {
    // Require Supabase auth session due to FK to auth.users (in network schema)
    const { data: sessionData } = await supabase!.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) {
      return {
        data: null,
        error: new Error("Not signed in. Please sign in to create a studio."),
      };
    }

    const base = (name || "My Studio").trim() || "My Studio";
    const slug = makeUniqueSlug(base);

    const insert: Database["public"]["Tables"]["studios"]["Insert"] = {
      owner_user_id: userId,
      name: base,
      slug,
      is_public: isPublic,
    } as Database["public"]["Tables"]["studios"]["Insert"];

    const { data, error } = await upsertStudio(insert);
    if (error) throw error as any;

    // Ensure owner membership via API as safety (if upsertStudio path missed)
    try {
      const { data: sessionData } = await supabase!.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (token && data?.id) {
        await fetch('/api/memberships/upsert', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ action: 'ensureOwner', studioId: data.id, userId })
        });
      }
    } catch (e) {
      console.warn('[studiosApi] ensureOwner (createStudioForUser) failed (non-fatal):', (e as any)?.message || e);
    }

    return { data, error: null };
  } catch (error) {
    const msg = (error as any)?.message || String(error);
    console.error("Error creating studio for user:", msg, error);
    return { data: null, error };
  }
}

/**
 * Ensure a studio row exists for the given user_id (owner_user_id).
 * If missing, creates a minimal studio row so Stripe/status reads have a home.
 */
export async function ensureStudioRowForUser(
  userId: string,
  {
    name,
    isPublic = false,
  }: {
    name?: string;
    isPublic?: boolean;
  } = {},
) {
  try {
    const { data: existing, error: selErr } = await supabase!
      .from("studios")
      .select("*")
      .eq("owner_user_id", userId)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();

    if (selErr) throw selErr as any;
    if (existing?.id) return { data: existing, created: false, error: null };

    const base = (name || "My Studio").trim() || "My Studio";
    const slug = makeUniqueSlug(base);
    const payload: Database["public"]["Tables"]["studios"]["Insert"] = {
      owner_user_id: userId,
      name: base,
      slug,
      is_public: isPublic,
    } as Database["public"]["Tables"]["studios"]["Insert"];

    // NOTE: Caller must ensure a valid Supabase session exists (RLS requires auth.uid()).
    const { data, error } = await supabase!
      .from("studios")
      .upsert(payload, { onConflict: "owner_user_id" })
      .select()
      .single();

    if (error) throw error as any;
    // Ensure owner membership via API safety net
    try {
      const { data: sessionData } = await supabase!.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (token && data?.id) {
        await fetch('/api/memberships/upsert', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ action: 'ensureOwner', studioId: data.id, userId })
        });
      }
    } catch (e) {
      console.warn('[studiosApi] ensureOwner (ensureStudioRowForUser) failed (non-fatal):', (e as any)?.message || e);
    }

    return { data, created: true, error: null };
  } catch (error) {
    console.error("Error ensuring studio row for user:", error);
    return { data: null, created: false, error };
  }
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "")
    .slice(0, 48);
}

function makeUniqueSlug(name: string): string {
  const base = slugify(name || "my-studio") || "my-studio";
  const suffix = Math.random().toString(36).slice(2, 6);
  return `${base}-${suffix}`;
}

// =====================================================
// HELPER FUNCTIONS
// =====================================================

/**
 * Calculate distance between two coordinates (Haversine formula)
 * Returns distance in kilometers
 */
function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}
